/**
 * Shared Enter-to-send policy for messaging / content composers.
 *
 * - Ctrl/Cmd+Enter always submits (posts, comments, cards, and DMs).
 * - Plain Enter submits only in message mode on desktop outside lists/code.
 * - Structural contexts (lists, code blocks) defer to the editor for plain Enter.
 *
 * @param {KeyboardEvent|{key:string,ctrlKey?:boolean,metaKey?:boolean,shiftKey?:boolean,isComposing?:boolean,keyCode?:number}} event
 * @param {{ isMobile?: boolean, inCodeBlock?: boolean, inListItem?: boolean, plainEnterSubmits?: boolean }} [opts]
 */
export function shouldSubmitOnEnter(event, opts = {}) {
  if (!event || event.key !== "Enter") return false;
  if (event.isComposing || event.keyCode === 229) return false;

  // Modifier submit wins before structural / mobile / plain-Enter policy.
  if (event.ctrlKey || event.metaKey) return true;
  if (event.shiftKey) return false;

  // Posts / comments / cards: Enter is always a newline unless modified.
  if (opts.plainEnterSubmits === false) return false;

  if (opts.inCodeBlock || opts.inListItem) return false;
  if (opts.isMobile) return false;

  return true;
}

/**
 * Detect a Slack/Markdown-style list prefix on a single paragraph.
 * e.g. "1. test", "- hello", "* item"
 */
export function matchMarkdownListPrefix(text) {
  if (text == null) return null;
  const value = String(text);
  const ordered = value.match(/^(\d+)\.\s+(.*)$/);
  if (ordered) {
    return {
      type: "ordered",
      start: Number(ordered[1]),
      prefixLength: ordered[0].length - ordered[2].length,
      body: ordered[2],
    };
  }
  const bullet = value.match(/^([-*+])\s+(.*)$/);
  if (bullet) {
    return {
      type: "bullet",
      prefixLength: bullet[0].length - bullet[2].length,
      body: bullet[2],
    };
  }
  return null;
}

/**
 * Detect a list trigger waiting for Space: "1." / "-" / "*" / "+"
 * (TipTap's wrapping input rule; we also handle this explicitly for reliability).
 */
export function matchMarkdownListTrigger(text) {
  if (text == null) return null;
  const value = String(text);
  const ordered = value.match(/^(\d+)\.$/);
  if (ordered) {
    return { type: "ordered", start: Number(ordered[1]) };
  }
  if (/^[-*+]$/.test(value)) {
    return { type: "bullet" };
  }
  return null;
}

function canPromoteListInParagraph(editor) {
  if (!editor || editor.isDestroyed) return false;
  if (
    editor.isActive("listItem") ||
    editor.isActive("bulletList") ||
    editor.isActive("orderedList") ||
    editor.isActive("codeBlock") ||
    editor.isActive("code")
  ) {
    return false;
  }
  const { state } = editor;
  if (!state?.selection?.$from) return false;
  const { $from } = state.selection;
  if (!$from.parent?.isTextblock) return false;
  if ($from.parent.type.name !== "paragraph") return false;
  return true;
}

/**
 * Resolve the soft-wrapped "line" inside a paragraph (Shift+Enter inserts
 * hardBreak nodes; list triggers must match that line, not the whole block).
 * @param {import('@tiptap/pm/model').ResolvedPos} $pos
 * @param {import('@tiptap/pm/model').Node} doc
 * @returns {{ from: number, to: number, text: string, parentStart: number } | null}
 */
export function getParagraphLineAt($pos, doc) {
  const parent = $pos?.parent;
  if (!parent?.isTextblock || parent.type.name !== "paragraph") return null;
  if (!doc) return null;

  const parentStart = $pos.start();
  const lines = [];
  let lineStart = parentStart;

  parent.forEach((child, offset) => {
    const from = parentStart + offset;
    if (child.type.name === "hardBreak") {
      lines.push({ from: lineStart, to: from });
      lineStart = from + child.nodeSize;
    }
  });
  lines.push({ from: lineStart, to: parentStart + parent.content.size });

  const line =
    lines.find((l) => $pos.pos >= l.from && $pos.pos <= l.to) ||
    lines[lines.length - 1];

  return {
    from: line.from,
    to: line.to,
    text: doc.textBetween(line.from, line.to),
    parentStart,
  };
}

function applyListType(chain, match) {
  if (match.type === "ordered") {
    chain.toggleOrderedList();
    if (Number.isFinite(match.start) && match.start > 1) {
      chain.updateAttributes("orderedList", { start: match.start });
    }
  } else {
    chain.toggleBulletList();
  }
  return chain;
}

/**
 * On Space after "1." / "-", convert the current line into a real list item.
 * Mirrors TipTap OrderedList/BulletList input rules so compose (Enter-to-send)
 * gets the same Slack-style auto-list behavior as edit surfaces.
 * Handles Shift+Enter soft-breaks: only the current line must be the trigger.
 * @returns {boolean} true if handled
 */
export function promoteMarkdownListTriggerOnSpace(editor) {
  if (!canPromoteListInParagraph(editor)) return false;

  const { state } = editor;
  const { $from } = state.selection;
  const line = getParagraphLineAt($from, state.doc);
  if (!line) return false;
  // Only when the cursor is at the end of this line (typing "1." + Space)
  if ($from.pos !== line.to) return false;

  const match = matchMarkdownListTrigger(line.text);
  if (!match) return false;

  const chain = editor.chain().focus();
  const isFirstLine = line.from === line.parentStart;

  if (isFirstLine) {
    chain.deleteRange({ from: line.from, to: line.to });
  } else {
    // Remove the preceding hardBreak + trigger, split into a new paragraph,
    // then listify that empty paragraph.
    chain
      .deleteRange({ from: line.from - 1, to: line.to })
      .splitBlock();
  }

  applyListType(chain, match);
  return chain.run();
}

/**
 * If the current line looks like "1. text" / "- text", turn it into a
 * real list item and open a fresh following item (Slack/Teams Enter behavior).
 * @returns {boolean} true if handled
 */
export function promoteMarkdownListLine(editor) {
  if (!canPromoteListInParagraph(editor)) return false;

  const { state } = editor;
  const { $from } = state.selection;
  const line = getParagraphLineAt($from, state.doc);
  if (!line) return false;

  const match = matchMarkdownListPrefix(line.text);
  if (!match) return false;

  const chain = editor.chain().focus();
  const isFirstLine = line.from === line.parentStart;

  if (isFirstLine) {
    if (match.prefixLength > 0) {
      chain.deleteRange({ from: line.from, to: line.from + match.prefixLength });
    }
    applyListType(chain, match);
  } else {
    // Lift this soft-broken line into its own paragraph, then listify.
    // Delete hardBreak + full line, split, re-insert body, wrap as list.
    chain.deleteRange({ from: line.from - 1, to: line.to }).splitBlock();
    if (match.body) {
      chain.insertContent(match.body);
    }
    applyListType(chain, match);
  }

  chain.splitListItem("listItem");
  return chain.run();
}
